import { Typography } from 'antd';
import { ButtonOrLinkComponent } from '../ButtonOrLinkComponent';
import { getBackgroundUrl } from '../../../../../../../../lib/util/helpers';
import styles from './styles.module.scss';
import { InfoIconComponent } from '../InfoIconComponent';
import { useVariables } from 'src/processes/CMS/processes/management/pages/PreviewPage/hooks/useVariables';
import { AppCheckbox } from '../../../../../../../../ui/AppCheckbox';
import { AppFormSection } from '../../../../../../../../ui/AppFormSection';
import { getCustomerCodePrefillApiRequest } from '../../../../../../../../lib/api';
import {
  applyProductPrefillToPagesFlow,
  getCustomerPrefillRuntimeReset,
  getFinderUrlForPrefill,
  getPrefillErrorMessage,
  getProductPrefillSource,
  isCustomerCodeWithinLimit,
  sanitizeCustomerCode,
} from '../../lib/prefill';
import { useLocalization } from 'src/lib/hooks/useLocalization';
import { hasRichTextContent } from 'src/lib/util/rich-text';
import { resetHvmSubmissionId } from 'src/processes/CMS/lib/hvm/questionnaire-result';

const ERROR_COLOR = '#D14343';

// eslint-disable-next-line max-lines-per-function
export function PreviewIntroLayout({
  media,
  introObject,
  colors,
  fonts,
  style,
  setPagesFlow,
  pagesFlow,
  setPageHistory,
  setShowChapterIds,
  currentElements,
}) {
  const { applyVariable } = useVariables(pagesFlow);
  const { preview } = useLocalization().translations.unique.components;

  const { content: { headline, primaryButton, secondaryButton, selectBackgroundImage, subline, customerCode = {}, privacyNotice = {} } = {} } =
    introObject || {};

  const { rootStyle = {}, headlineStyle = {}, sublineStyle = {}, primaryButtonStyle = {}, secondaryButtonStyle = {} } = style || {};

  const backgroundImageUrl = getBackgroundUrl(selectBackgroundImage, media);
  const customerCodeValue = pagesFlow?.introCustomerCode || '';
  const introPrivacyAccepted = Boolean(pagesFlow?.introPrivacyAccepted);
  const shouldRequirePrivacyConsent = Boolean(privacyNotice?.checkbox?.enabled);
  const shouldShowCustomerCode = Boolean(customerCode?.enabled);
  const hasPrivacyNoticeContent = hasRichTextContent(privacyNotice?.content);

  const resetCustomerSessionUiState = () => {
    const baselineChapters = pagesFlow?.productPrefillBaseline?.allChapters ?? pagesFlow?.allChapters ?? [];

    setPageHistory?.([]);
    // A new customer in the same iframe must not overwrite the result saved in HVM for the previous one.
    resetHvmSubmissionId();
    setShowChapterIds?.(baselineChapters.filter(chapter => !chapter.content?.completedChaptersButtons).map(chapter => chapter.id));
  };

  const applyCustomerPrefill = (productStatuses, customerCodeToApply) => {
    resetCustomerSessionUiState();
    setPagesFlow(prev => {
      const updatedPrefillState = applyProductPrefillToPagesFlow({
        allPages: prev?.allPages,
        allChapters: prev?.allChapters,
        productStatuses,
        productPrefillBaseline: prev?.productPrefillBaseline,
      });

      return {
        ...prev,
        ...getCustomerPrefillRuntimeReset(prev),
        ...updatedPrefillState,
        introCustomerCode: customerCodeToApply,
        introPrivacyAccepted: Boolean(introPrivacyAccepted),
        introPrefillLoading: false,
        introPrefillError: null,
        introPrefillApplied: true,
      };
    });
  };

  const clearCustomerPrefill = () => {
    resetCustomerSessionUiState();
    setPagesFlow(prev => {
      const resetPrefillState = prev?.introPrefillApplied ? getProductPrefillSource(prev) : {};

      return {
        ...prev,
        ...getCustomerPrefillRuntimeReset(prev),
        ...resetPrefillState,
        introCustomerCode: '',
        introPrivacyAccepted: Boolean(introPrivacyAccepted),
        introPrefillLoading: false,
        introPrefillError: null,
        introPrefillApplied: false,
      };
    });
  };

  const handleBeforeClick = async () => {
    if (shouldRequirePrivacyConsent && !introPrivacyAccepted) {
      setPagesFlow(prev => ({
        ...prev,
        introPrivacyError: preview.prefill.privacyConsentRequired(),
      }));

      return false;
    }

    // A code can also arrive from the finder link, while the intro may have the field switched off
    // in the CMS. Prefilling from a value the user cannot see or correct would be a silent action,
    // so the code counts only while the field is on.
    const sanitizedCustomerCode = shouldShowCustomerCode ? sanitizeCustomerCode(customerCodeValue) : '';

    if (sanitizedCustomerCode && !isCustomerCodeWithinLimit(sanitizedCustomerCode)) {
      setPagesFlow(prev => ({
        ...prev,
        introPrefillError: preview.prefill.customerCodeTooLong(),
      }));

      return false;
    }

    setPagesFlow(prev => ({ ...prev, introPrefillLoading: true, introPrefillError: null }));

    try {
      if (sanitizedCustomerCode) {
        const { data } = await getCustomerCodePrefillApiRequest({
          customerCode: sanitizedCustomerCode,
          finderUrl: getFinderUrlForPrefill({ finderUrl: pagesFlow?.finder?.url }),
        });

        const productStatuses = data?.membershipShares ?? {};

        if (!data?.success || typeof productStatuses !== 'object' || Array.isArray(productStatuses)) {
          throw new Error('PREFILL_RESPONSE_INVALID');
        }

        applyCustomerPrefill(productStatuses, sanitizedCustomerCode);

        return true;
      }

      clearCustomerPrefill();

      return true;
    } catch (error) {
      // The fallback covers both causes on purpose: the upstream service reports an unknown
      // customer code and a real outage with the same HTTP 500 and only a German free-text detail,
      // so the two are not reliably distinguishable. The user action is identical either way.
      setPagesFlow(prev => ({
        ...prev,
        introPrefillLoading: false,
        introPrefillError: getPrefillErrorMessage(error, preview.prefill.requestFailed()),
      }));

      return false;
    }
  };

  return (
    <section
      className={styles.section}
      style={{
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundImage: `url('${backgroundImageUrl}')`,
        width: '100%',
        height: 'calc(100vh - 94px)',
        position: 'relative',
      }}
    >
      <div style={{ position: 'absolute', right: 80, top: 10 }}>
        <InfoIconComponent applyVariable={applyVariable} type={selectBackgroundImage} />
      </div>
      <div className={styles.container}>
        <div
          className={styles.card}
          style={{
            minHeight: 269,
            background: colors.backgroundColor,
            padding: 24,
            ...rootStyle,
          }}
        >
          {subline && (
            <div style={{ display: 'flex' }}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{
                  marginBottom: 16,
                  overflow: 'hidden',
                  wordBreak: 'break-all',
                  fontSize: fonts.body.fontSize,
                  fontFamily: fonts.body.font,
                  fontWeight: fonts.body.fontWeight,
                  color: colors[fonts.body.colorVariant],
                  minHeight: fonts.body.fontSize,
                  ...sublineStyle,
                  maxWidth: '95%',
                }}
              >
                {applyVariable(subline.value)}
              </Typography.Paragraph>
              <InfoIconComponent applyVariable={applyVariable} type={subline} styles={{ top: 0 }} />
            </div>
          )}
          {headline && (
            <div style={{ display: 'flex' }}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{
                  ...fonts.headlineSecond,
                  marginBottom: 32,
                  overflow: 'hidden',
                  wordBreak: 'break-all',
                  fontSize: fonts.headlineSecond.fontSize,
                  fontFamily: fonts.headlineSecond.font,
                  fontWeight: fonts.headlineSecond.fontWeight,
                  color: colors[fonts.headlineSecond.colorVariant],
                  minHeight: fonts.headlineSecond.fontSize,
                  ...headlineStyle,
                  maxWidth: '95%',
                }}
              >
                {applyVariable(headline.value)}
              </Typography.Paragraph>
              <InfoIconComponent applyVariable={applyVariable} type={headline} styles={{ top: 5 }} />
            </div>
          )}
          {shouldShowCustomerCode && (
            <div style={{ marginBottom: 20 }}>
              <AppFormSection
                textFieldProps={{
                  label: applyVariable(customerCode?.headline?.value) || customerCode?.headline?.value || '',
                  value: customerCodeValue || '',
                  maxLength: 16,
                  disabled: pagesFlow?.introPrefillLoading,
                  onChange: event =>
                    setPagesFlow(prev => ({
                      ...prev,
                      introCustomerCode: event.target.value,
                      introPrefillError: null,
                    })),
                }}
              />
            </div>
          )}
          {pagesFlow?.introPrefillError && (
            <Typography.Paragraph style={{ color: ERROR_COLOR, marginBottom: 16 }}>{pagesFlow.introPrefillError}</Typography.Paragraph>
          )}
          {(hasPrivacyNoticeContent || privacyNotice?.checkbox?.enabled) && (
            <div style={{ marginBottom: 16 }}>
              {hasPrivacyNoticeContent && (
                <Typography.Paragraph style={{ marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: applyVariable(privacyNotice.content),
                    }}
                  />
                </Typography.Paragraph>
              )}
              {privacyNotice?.checkbox?.enabled && (
                <AppCheckbox
                  checked={introPrivacyAccepted}
                  onChange={event =>
                    setPagesFlow(prev => ({
                      ...prev,
                      introPrivacyAccepted: event.target.checked,
                      introPrivacyError: null,
                    }))
                  }
                  textValue={applyVariable(privacyNotice?.checkbox?.label) || privacyNotice?.checkbox?.label}
                />
              )}
              {pagesFlow?.introPrivacyError && (
                <Typography.Paragraph style={{ color: ERROR_COLOR, marginBottom: 0, marginTop: 8 }}>
                  {pagesFlow.introPrivacyError}
                </Typography.Paragraph>
              )}
            </div>
          )}
          {primaryButton && (
            <div style={{ position: 'relative' }}>
              <ButtonOrLinkComponent
                contentObject={primaryButton}
                style={{
                  width: 300,
                  minHeight: 48,
                  background: colors.primaryColor,
                  marginBottom: 10,
                  border: 'none',
                  borderRadius: 24,
                  color: '#FFFFFF',
                  ...primaryButtonStyle,
                  position: 'relative',
                }}
                setPagesFlow={setPagesFlow}
                pagesFlow={pagesFlow}
                textStyle={{
                  fontSize: fonts.body.fontSize,
                  fontFamily: fonts.body.font,
                  fontWeight: fonts.body.fontWeight,
                }}
                disabled={pagesFlow?.introPrefillLoading}
                setPageHistory={setPageHistory}
                currentElements={currentElements}
                beforeClick={handleBeforeClick}
              >
                <InfoIconComponent applyVariable={applyVariable} type={primaryButton} styles={{ top: -8, right: -15 }} />
              </ButtonOrLinkComponent>
            </div>
          )}
          {secondaryButton && (
            <ButtonOrLinkComponent
              contentObject={secondaryButton}
              style={{
                width: 300,
                minHeight: 48,
                background: '#FFFFFF',
                marginBottom: 10,
                border: '1px solid #FFFFFF',
                borderRadius: 24,
                color: '#191919',
                ...secondaryButtonStyle,
                position: 'relative',
              }}
              setPagesFlow={setPagesFlow}
              pagesFlow={pagesFlow}
              textStyle={{
                fontSize: fonts.body.fontSize,
                fontFamily: fonts.body.font,
                fontWeight: fonts.body.fontWeight,
              }}
              disabled={pagesFlow?.introPrefillLoading}
              setPageHistory={setPageHistory}
              currentElements={currentElements}
              beforeClick={handleBeforeClick}
            >
              <InfoIconComponent applyVariable={applyVariable} type={secondaryButton} styles={{ top: -8, right: -15 }} />
            </ButtonOrLinkComponent>
          )}
        </div>
      </div>
    </section>
  );
}
